import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import { Box, Paper, Stack, Tooltip, Typography } from "@mui/material";
import { useVideoControlState } from "components/scrapEyeDataDashboard/VideoPlayer/contexts/VideoControlContext";
import dayjs, { Dayjs } from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import { Fetching } from "src/components/common/loading/fetching";
import { useTenantTranslation } from "hooks/formatters";
import { useThrottledValue } from "hooks/useThrottledValue";
import { memo, useMemo } from "react";
import { useRailcarMovementsBetween } from "hooks/useRailcarMovements";
import { RailcarMovement } from "src/store/api/generatedApi";
import { groupByTime } from "./RailcarTimeline";

dayjs.extend(relativeTime);

const CURRENT_TIME_THROTTLE_MS = 500;

interface RailcarMovementsProps {
  startTime: Dayjs;
  endTime: Dayjs;
}

const GROUP_GAP_MINUTES = 5;

const RailcarMovementsInner = ({
  startTime,
  endTime,
}: RailcarMovementsProps) => {
  const { t } = useTenantTranslation();
  const { railcarMovements, isFetching } = useRailcarMovementsBetween(
    startTime,
    endTime
  );
  const { currentTime: currentTimeSeconds } = useVideoControlState();
  const throttledCurrentTimeSeconds = useThrottledValue(
    currentTimeSeconds,
    CURRENT_TIME_THROTTLE_MS
  );
  const currentTime = useMemo(
    () => startTime.add(throttledCurrentTimeSeconds, "second"),
    [startTime, throttledCurrentTimeSeconds]
  );

  const railcarGroupsInShop = useMemo(() => {
    const timedRailcars = railcarMovements.railcar_movements
      .map((movement) => ({
        time: dayjs(movement.inbound_time),
        railcar: movement,
      }))
      .sort((a, b) => a.time.diff(b.time));
    // first group railcars by inbound time
    const groupedRailcars = groupByTime(timedRailcars, GROUP_GAP_MINUTES);
    return groupedRailcars.flatMap((group) => {
      const inShop = group
        .filter(
          (r) => r.railcar.outbound_time == null && r.time.isBefore(currentTime)
        )
        .map((r) => r.railcar);

      const departed = group
        .filter((r) => r.railcar.outbound_time != null)
        .map((r) => ({
          time: dayjs(r.railcar.outbound_time),
          railcar: r.railcar,
        }))
        .sort((a, b) => a.time.diff(b.time));

      const departedStillHere = groupByTime(departed, GROUP_GAP_MINUTES)
        .filter(
          (g) =>
            g.some((r) => r.time.isAfter(currentTime)) &&
            g.some((r) => dayjs(r.railcar.inbound_time).isBefore(currentTime))
        )
        .flat()
        .map((r) => r.railcar);

      const combined = [...departedStillHere, ...inShop];
      return combined.length > 0 ? [combined] : [];
    });
    // eslint-disable-next-line @eslint-react/exhaustive-deps -- use .valueOf() to avoid new Dayjs object identity on every render
  }, [railcarMovements, currentTime.valueOf()]);

  const railcarCounts = useMemo(() => {
    return Object.entries(
      railcarGroupsInShop.reduce<Record<string, number>>((acc, group) => {
        group.forEach((railcar) => {
          const materialName =
            railcar.material.labelling_name ?? railcar.material.name;
          acc[materialName] = (acc[materialName] ?? 0) + 1;
        });
        return acc;
      }, {})
    );
  }, [railcarGroupsInShop]);

  return (
    <Fetching fetching={isFetching}>
      <Stack
        sx={{
          display: "flex",
          flexDirection: "column",
          width: "100%",
          height: "100%",
          minHeight: 0,
        }}
      >
        <Typography variant="h5" color="primary">
          {t("railcarMovementsTitle")}
        </Typography>
        <Box sx={{ flex: 1, minHeight: 0, overflowY: "auto" }}>
          {railcarMovements.railcar_movements.length === 0 ? (
            <Paper sx={{ p: 3, textAlign: "center" }}>
              <Typography variant="body2" color="text.secondary">
                {t("railcarMovementsNoMovements")}
              </Typography>
            </Paper>
          ) : (
            <Stack spacing={0.5} direction="row" display="flex">
              <RailcarCountsComponent railcarCounts={railcarCounts} />
              <RailcarGroupsComponent
                railcarGroups={railcarGroupsInShop}
                currentTime={currentTime}
              />
            </Stack>
          )}
        </Box>
      </Stack>
    </Fetching>
  );
};

export const RailcarMovements = memo(RailcarMovementsInner);

const RailcarCountsComponent = memo(
  ({ railcarCounts }: { railcarCounts: [string, number][] }) => (
    <Stack flex={1}>
      {railcarCounts.map(([materialName, count]) => (
        <Box key={materialName} sx={{ position: "relative" }}>
          <Typography variant="body2" color="text.secondary">
            {materialName} ({count})
          </Typography>
        </Box>
      ))}
    </Stack>
  )
);
RailcarCountsComponent.displayName = "RailcarCountsComponent";

const RailcarGroupsComponent = memo(
  ({
    railcarGroups,
    currentTime,
  }: {
    railcarGroups: RailcarMovement[][];
    currentTime: Dayjs;
  }) => (
    <Stack direction="column" spacing={0.5} flex={3} sx={{ overflowX: "auto" }}>
      {railcarGroups.map((group) => (
        <Stack
          key={group.map((r) => r.tag_id).join(",")}
          direction="row"
          spacing={0}
        >
          {group.map((railcar, index) => {
            const inboundTime = dayjs(railcar.inbound_time);
            const isOld =
              railcar.outbound_time == null &&
              currentTime.diff(inboundTime, "day") >= 1;
            return (
              <Stack
                key={railcar.tag_id}
                direction="row"
                spacing={0}
                alignItems="center"
              >
                <RailcarComponent
                  key={railcar.tag_id}
                  materialName={
                    railcar.material.labelling_name ?? railcar.material.name
                  }
                  tagIdDisplay={railcar.tag_id_display ?? railcar.tag_id}
                  inboundTime={isOld ? inboundTime : null}
                />
                {index < group.length - 1 && (
                  <Box sx={{ height: 5, width: 5, bgcolor: "grey.600" }} />
                )}
              </Stack>
            );
          })}
        </Stack>
      ))}
    </Stack>
  )
);
RailcarGroupsComponent.displayName = "RailcarGroupsComponent";

const RailcarComponent = memo(
  ({
    materialName,
    tagIdDisplay,
    inboundTime,
  }: {
    materialName: string;
    tagIdDisplay: string;
    inboundTime: Dayjs | null;
  }) => {
    const { t } = useTenantTranslation();
    const isOld = inboundTime != null;
    return (
      <Box
        sx={{
          position: "relative",
          px: 0.5,
          py: 0.5,
          width: 80,
          minWidth: 80,
          maxWidth: 80,
          flexShrink: 0,
          boxSizing: "border-box",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          border: 1,
          bgcolor: isOld ? "grey.200" : undefined,
        }}
      >
        <Stack direction="row" spacing={0.25} alignItems="center">
          <Typography
            variant="caption"
            color={isOld ? "text.secondary" : "text.primary"}
            sx={{ fontWeight: 500 }}
            noWrap
          >
            {materialName}
          </Typography>
          {isOld ? (
            <Tooltip
              title={t("railcarNoDeparture", { time: inboundTime.fromNow() })}
              arrow
            >
              <HelpOutlineIcon sx={{ fontSize: 14, color: "text.secondary" }} />
            </Tooltip>
          ) : null}
        </Stack>
        <Typography
          variant="caption"
          color="text.secondary"
          noWrap
          title={tagIdDisplay}
        >
          {tagIdDisplay}
        </Typography>
      </Box>
    );
  }
);
RailcarComponent.displayName = "RailcarComponent";
